import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@material-ui/core';
import { InView } from 'react-intersection-observer';
import * as Sentry from '@sentry/react';

import { ContentFragment } from 'api';
import { UserGroup } from 'types/app';
import { PaddedPaper } from 'components';
import { FeedItem } from 'features/content';

interface IProps {
  selectedGroup: UserGroup;
  results: ContentFragment[];
  onResults: () => void;
  onRequest: () => void;
}

const THRESHOLD = [0, 0.25, 0.5, 1];

const Page: FC<IProps> = props => {
  const { selectedGroup, results, onResults, onRequest } = props;
  const { t } = useTranslation();
  const [position] = useState({
    topPreviousY: 0,
    topPreviousRatio: 0,
    bottomPreviousY: 0,
    bottomPreviousRatio: 0,
  });

  function handleFeedError() {
    return (
      <PaddedPaper>
        <Typography>{t('errors:feed')}</Typography>
      </PaddedPaper>
    );
  }

  function isTop(index: number) {
    return index === 0;
  }

  function isBottom(index: number) {
    return index === results.length - 1;
  }

  function handleTopCallback(entry: IntersectionObserverEntry) {
    const { topPreviousY, topPreviousRatio } = position;

    const { boundingClientRect, isIntersecting } = entry;
    const currentY = boundingClientRect.top;
    const currentRatio = entry.intersectionRatio;

    const hasGreaterY = currentY > topPreviousY;
    const hasGreaterRatio = currentRatio >= topPreviousRatio;
    const scrollingUp = hasGreaterY && hasGreaterRatio && isIntersecting;

    scrollingUp ? onResults() : onRequest();

    position.topPreviousY = currentY;
    position.topPreviousRatio = currentRatio;
  }

  function handleBottomCallback(entry: IntersectionObserverEntry) {
    const { bottomPreviousY, bottomPreviousRatio } = position;
    const { boundingClientRect, isIntersecting } = entry;
    const currentY = boundingClientRect.top;
    const currentRatio = entry.intersectionRatio;

    const hasSmallerY = currentY < bottomPreviousY;
    const hasGreaterRatio = currentRatio > bottomPreviousRatio;
    const scrollingDown = hasSmallerY && hasGreaterRatio && isIntersecting;

    scrollingDown ? onRequest() : onResults();

    position.bottomPreviousY = currentY;
    position.bottomPreviousRatio = currentRatio;
  }

  function handleChange(top: boolean, entry: IntersectionObserverEntry) {
    top ? handleTopCallback(entry) : handleBottomCallback(entry);
  }

  function renderTrackedItem(result: ContentFragment, top: boolean) {
    return (
      <InView
        key={result.content_id}
        threshold={THRESHOLD}
        onChange={(inView, entry) => {
          if (inView) {
            handleChange(top, entry);
          }
        }}>
        {({ ref }) => <div ref={ref}>{renderItem(result)}</div>}
      </InView>
    );
  }

  function renderItem(result: ContentFragment) {
    return (
      <Sentry.ErrorBoundary key={result.content_id} fallback={handleFeedError}>
        <FeedItem content={result} selectedGroup={selectedGroup} />
      </Sentry.ErrorBoundary>
    );
  }

  return (
    <Fragment>
      {results.map((result, index) => {
        const top = isTop(index);
        const bottom = isBottom(index);

        if (top || bottom) {
          return renderTrackedItem(result, top);
        }

        return renderItem(result);
      })}
    </Fragment>
  );
};

export default Page;
